'use client'

import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react'

interface TouchableContextProps {
  isTouchable: boolean
  canHover: boolean
  isHoverableOnly: boolean
}

const TouchableContext = createContext<TouchableContextProps | undefined>(undefined)

export const TouchableProvider = ({ children }: { children: ReactNode }) => {
  const [isTouchable, setIsTouchable] = useState<boolean>(false)
  const [canHover, setCanHover] = useState<boolean>(false)

  const isHoverableOnly = useMemo(() => {
    return !isTouchable && canHover
  }, [isTouchable, canHover])

  useEffect(() => {
    const checkTouchable = () => {
      // check if device supports touch events
      const hasTouchSupport =
        'ontouchstart' in window || navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0

      // check for coarse pointer media query (typically mobile/touch devices)
      const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches

      // check for hover capability
      const hasHoverCapability = window.matchMedia('(hover: hover)').matches

      setIsTouchable(hasTouchSupport || hasCoarsePointer)
      setCanHover(hasHoverCapability)
    }

    checkTouchable()

    // set up media query listeners for pointer type and hover capability changes
    const pointerMediaQuery = window.matchMedia('(pointer: coarse)')
    const hoverMediaQuery = window.matchMedia('(hover: hover)')

    // define handler function
    const handleMediaChange = () => {
      checkTouchable()
    }

    // add event listeners
    if (pointerMediaQuery.addEventListener) {
      pointerMediaQuery.addEventListener('change', handleMediaChange)
      hoverMediaQuery.addEventListener('change', handleMediaChange)
    } else {
      // fallback for older browsers
      pointerMediaQuery.addListener(handleMediaChange)
      hoverMediaQuery.addListener(handleMediaChange)
    }

    // clean up
    return () => {
      if (pointerMediaQuery.removeEventListener) {
        pointerMediaQuery.removeEventListener('change', handleMediaChange)
        hoverMediaQuery.removeEventListener('change', handleMediaChange)
      } else {
        // fallback for older browsers
        pointerMediaQuery.removeListener(handleMediaChange)
        hoverMediaQuery.removeListener(handleMediaChange)
      }
    }
  }, [])

  return (
    <TouchableContext.Provider value={{ isTouchable, canHover, isHoverableOnly }}>{children}</TouchableContext.Provider>
  )
}

export const useTouchable = () => {
  const context = useContext(TouchableContext)
  if (!context) {
    throw new Error('useTouchable must be used within a TouchableProvider')
  }
  return context
}
